/**
 * Application-specific wrapper for useApi hook with singleton instance
 * 
 * This wrapper creates a SINGLE shared instance of the API client at the app level,
 * preventing multiple token exchanges and cache duplications.
 * 
 * The library (@revalize/api-client) remains flexible - other apps can:
 * - Use the raw useApi hook directly (multi-instance)
 * - Create their own context pattern (singleton like this)
 * - Use any other state management approach
 * 
 * Usage:
 * ```typescript
 * // 1. ApiProvider is already set up in App.tsx
 * 
 * // 2. Use in any component
 * import { useApi } from '@/hooks/useAppApi'
 * 
 * function MyComponent() {
 *   const api = useApi()
 *   // All components share the same instance!
 * }
 * ```
 */

import { createContext, useContext, type ReactNode } from 'react'
import { useApi as useLibraryApi, type UseApiReturn } from '@revalize/api-client/frontend'
import { ConfigService } from '@/services/ConfigService'

// Context for sharing single API instance across app
const ApiContext = createContext<UseApiReturn | null>(null)

/**
 * Provider component that creates ONE shared API instance
 * This is placed at the app level to ensure all components share the same:
 * - Token exchange manager
 * - Token cache
 * - Auth state
 * 
 * Benefits:
 * - Only ONE token exchange request on app load
 * - Consistent auth state across all components
 * - Memory efficient (single cache)
 * - Easy to debug (single instance to inspect)
 */
export function ApiProvider({ children }: { children: ReactNode }): JSX.Element {
  const api = useLibraryApi(ConfigService.getApiBaseUrl())
  
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
}

/**
 * Hook to access the shared API instance (singleton)
 * Throws an error if used outside ApiProvider
 * 
 * @returns Shared API client instance with:
 * - HTTP methods (get, post, put, delete, patch, request)
 * - Auth methods (signIn, signOut, getCurrentUser)
 * - Auth state (isAuthenticated, user, authError, isInitializing)
 */
export function useApi(): UseApiReturn {
  const api = useContext(ApiContext)
  
  if (!api) {
    throw new Error('useApi must be used within ApiProvider. Make sure your component is wrapped with <ApiProvider>.')
  }
  
  return api
}

